import React from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { BadgeCheck, Film, Eye, Link2, MapPin, ExternalLink, Sparkles } from "lucide-react"
import { INFLUENCER_PLATFORMS, INFLUENCER_TIER_LABEL } from "../constants"
import { formatViews } from "../utils"
import { useAnalytics, ANALYTICS_EVENTS } from "../../../analytics"
import Avatar from "./Avatar"
import FavouriteButton from "./FavouriteButton"

/**
 * One row of the Top Influencer leaderboard (or, in "My Favourites" mode,
 * one saved creator - same row shape either way). Clicking the card opens
 * the internal profile detail screen
 * (INFLUENCER_PROFILE_API.md) at `/influencer-explorer/:platform/:username`
 * - same "click opens internal detail" pattern as UGC Explorer's own
 * VideoCard, just landing on a real route here instead of an in-page panel
 * swap, since a creator profile is meant to be shareable/bookmarkable. The
 * row's own `profile_url` rides along as router state so the profile page
 * doesn't have to re-derive it; the outbound "visit on <platform>" link and
 * the favourite heart both stop propagation so they act independently of
 * that navigation.
 *
 * The current URL - path *and* query string - rides along too, as `from`.
 * InfluencerSection mirrors its filters/sort/page/mode onto that query
 * string (urlState.js), so capturing it here and having the profile
 * screen's Back link return to it (InfluencerProfileSection) is what makes
 * "filter the list, open a card, come back" land on the same filtered view
 * instead of a reset one - the list only resets when there's nowhere to
 * come back to (a direct visit or a shared link).
 */
const InfluencerCard = ({ influencer }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { trackEvent } = useAnalytics()
  const platformMeta = INFLUENCER_PLATFORMS.find(p => p.id === influencer.platform)
  const tierLabel = INFLUENCER_TIER_LABEL[influencer.creator_size]

  const openProfile = () => {
    trackEvent(ANALYTICS_EVENTS.FEATURE.INFLUENCER_PROFILE_VIEWED, {
      platform: influencer.platform,
      username: influencer.username,
    })
    navigate(`/influencer-explorer/${influencer.platform}/${encodeURIComponent(influencer.username)}`, {
      state: { profileUrl: influencer.profile_url, from: `${location.pathname}${location.search}` },
    })
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={openProfile}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openProfile() } }}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        background: "#1c1d27",
        border: "1px solid rgba(255,255,255,0.06)",
        borderRadius: "16px",
        padding: "16px",
        textDecoration: "none",
        color: "inherit",
        height: "100%",
        position: "relative",
        cursor: "pointer",
        transition: "transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-3px)"
        e.currentTarget.style.boxShadow = "0 12px 28px rgba(0,0,0,0.35)"
        e.currentTarget.style.borderColor = "rgba(246,37,90,0.35)"
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "none"
        e.currentTarget.style.boxShadow = "none"
        e.currentTarget.style.borderColor = "rgba(255,255,255,0.06)"
      }}
    >
      <FavouriteButton
        platform={influencer.platform}
        username={influencer.username}
        isFavourite={influencer.is_favourite}
        style={{ position: "absolute", top: "12px", right: "12px", zIndex: 1 }}
      />

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px", paddingRight: "40px" }}>
        {platformMeta && (
          <span style={{
            display: "flex", alignItems: "center", gap: "5px",
            background: `${platformMeta.color}18`, color: platformMeta.color,
            fontSize: "10.5px", fontWeight: 700, padding: "3px 8px", borderRadius: "6px",
          }}>
            <i className={platformMeta.icon} style={{ fontSize: "10px" }} />
            {platformMeta.label}
          </span>
        )}
        {tierLabel && (
          <span style={{
            fontSize: "10.5px", fontWeight: 700, color: "#9ca3af",
            background: "rgba(255,255,255,0.05)", padding: "3px 8px", borderRadius: "6px",
          }}>
            {tierLabel}
          </span>
        )}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <Avatar src={influencer.profile_image_url} alt={influencer.username} size={44} placeholder />
        <div style={{ minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <span
              style={{
                color: "#fff", fontSize: "14px", fontWeight: 600, fontFamily: "Rubik",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0,
              }}
              title={influencer.full_name || influencer.username}
            >
              {influencer.full_name || `@${influencer.username}`}
            </span>
            {influencer.is_verified && (
              <BadgeCheck size={14} color="#3b82f6" style={{ flexShrink: 0 }} title="Verified account" />
            )}
          </div>
          <span style={{ color: "#9ca3af", fontSize: "12px" }}>@{influencer.username}</span>
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", color: "#e8e8e8", fontWeight: 600 }}>
        {influencer.audience_display || formatViews(influencer.audience_size)} {influencer.audience_type || "audience"}
      </div>

      {influencer.description && (
        <p
          style={{
            color: "#adb5bd", fontSize: "12px", lineHeight: "1.5", margin: 0,
            display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
          }}
        >
          {influencer.description}
        </p>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap", fontSize: "11.5px", color: "#9ca3af", marginTop: "auto" }}>
        {(influencer.posts_count != null || influencer.videos_count != null) && (
          <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <Film size={11} /> {formatViews(influencer.posts_count ?? influencer.videos_count)} posts
          </span>
        )}
        {influencer.total_views != null && (
          <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <Eye size={11} /> {formatViews(influencer.total_views)} views
          </span>
        )}
        {influencer.location && (
          <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <MapPin size={11} /> {influencer.location}
          </span>
        )}
      </div>

      {(influencer.posts_about_apps > 0 || (Array.isArray(influencer.external_links) && influencer.external_links.length > 0)) && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
          {influencer.posts_about_apps > 0 ? (
            <span
              title="How many of their posts our video stores hold about apps"
              style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "11px", color: "#9ca3af", fontWeight: 600 }}
            >
              <Sparkles size={11} /> Covers {influencer.posts_about_apps} video{influencer.posts_about_apps === 1 ? "" : "s"}
            </span>
          ) : <span />}
          {Array.isArray(influencer.external_links) && influencer.external_links.length > 0 && (
            <span style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "11px", color: "#6b7280" }}>
              <Link2 size={11} /> {influencer.external_links.length} link{influencer.external_links.length === 1 ? "" : "s"}
            </span>
          )}
        </div>
      )}

      {/* Outbound link to the real platform profile - kept as its own
          affordance (stopPropagation) rather than the whole card, now that
          clicking the card opens the internal profile screen instead. */}
      <a
        href={influencer.profile_url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => {
          e.stopPropagation()
          trackEvent(ANALYTICS_EVENTS.FEATURE.INFLUENCER_OUTBOUND_CLICKED, {
            platform: influencer.platform,
            username: influencer.username,
            link_type: "profile",
          })
        }}
        style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "11px", color: "#6b7280", marginTop: "2px", textDecoration: "none" }}
        onMouseEnter={(e) => (e.currentTarget.style.color = "#9ca3af")}
        onMouseLeave={(e) => (e.currentTarget.style.color = "#6b7280")}
      >
        <ExternalLink size={11} /> Visit on {platformMeta?.label || influencer.platform}
      </a>
    </div>
  )
}

export default InfluencerCard
